import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, CheckCircle2, Info, Minus, Plus } from "lucide-react";
import {
  FortAgentPurchaseResultCode,
  FortAgentSkuConfigItem,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatEtherWithDecimals, formatUsdPrefix } from "@/lib/utils";
import { formatUSDT } from "@/lib/mockData";
import { useBalanceV2 } from "@/hooks/api/useBalanceV2";
import { C } from "./homeTheme";
import { useFortAgentPurchaseSign } from "@/hooks/api/useFortAgentPurchaseSign";
import { showError } from "@/lib/toast";
import {
  readContract,
  simulateContract,
  switchChain,
  waitForTransactionReceipt,
} from "@wagmi/core";
import { wagmiConfig } from "@/config";
import {
  CCC_FUSD_ADDRESS,
  FORTTRADE_CONTRACT_ADDRESS,
  USDT_ADDRESS,
} from "@/config/env";
import { FortTradeAbi } from "@/abi/FortTrade.abi";
import { useConnection, useReadContract, useWriteContract } from "wagmi";
import { erc20Abi } from "viem";
import { parseChainError } from "@/lib/chainError";
import { useTranslation } from "react-i18next";
import useFusdtPrice from "@/hooks/api/useFusdPrice";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { useQueryClient } from "@tanstack/react-query";
import { AGENT_IMAGE_URL_ACTIVE } from "@/components/home/MyAgentsSection/constants";
import { bscChainId } from "@/config/wagmi";

const AGENT_MARKET_ACCENT_COLORS = [
  "#22c55e",
  "#22d3ee",
  "#d4a017",
  "#f59e0b",
  "#ef4444",
] as const;

export function BuyAgentDialog({
  agent,
  open,
  onClose,
  canMix,
}: {
  agent: FortAgentSkuConfigItem | null;
  open: boolean;
  onClose: () => void;
  canMix: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { address } = useConnection();
  const writeContract = useWriteContract();
  const { data: fusdtPrice } = useFusdtPrice();
  const { getTokenBalanceData, isLoading: balanceV2Loading } = useBalanceV2();
  const fusdBalanceData = getTokenBalanceData(CCC_FUSD_ADDRESS);
  const userGameBalance = +formatEtherWithDecimals(
    fusdBalanceData?.gameFusdAmount ?? 0
  );
  const userPendingWithdrawal = +formatEtherWithDecimals(
    fusdBalanceData?.waitClaimFusdAmount ?? 0
  );
  const [buying, setBuying] = useState(false);
  const [step, setStep] = useState<"form" | "pending" | "success">("form");
  const [pendingKind, setPendingKind] = useState<"approve" | "pay">("pay");
  const [quantity, setQuantity] = useState(1);
  /** 老用户专享：`mixed` 延期补 FUSD；`mixed_now` 购买时立即扣 FUSD */
  const [mixedKind, setMixedKind] = useState<"mixed" | "mixed_now">(
    "mixed_now"
  );

  /** `canMix` 为 false → 仅全额 USDT；为 true → 用户可选 `mixed` / `mixed_now` */
  const paymentMode: "usdt" | "mixed" | "mixed_now" = canMix
    ? mixedKind
    : "usdt";
  const { mutateAsync: purchaseSignAsync } = useFortAgentPurchaseSign();
  const { data: usdtBalance } = useReadContract({
    abi: erc20Abi,
    address: USDT_ADDRESS as `0x${string}`,
    functionName: "balanceOf",
    args: [address!],
  });

  useEffect(() => {
    if (open && agent) {
      setQuantity(1);
      setMixedKind("mixed_now");
    }
  }, [open, agent?.skuId]);

  if (!agent) return null;

  const maxQty = Math.min(10, Math.max(0, agent.leftSupplyCount));
  const canDecQty = quantity > 1;
  const canIncQty = maxQty >= 1 && quantity < maxQty;
  const qtyBig = BigInt(quantity);
  // 标签颜色
  const tagColor =
    AGENT_MARKET_ACCENT_COLORS[
      Math.max(0, agent.tier - 1) % AGENT_MARKET_ACCENT_COLORS.length
    ];
  // 是否涨价
  const priceIncreased =
    BigInt(agent.basePriceUsdt) !== BigInt(agent.sellPriceUsdt);
  ///////////////////////均为1个agent 的价格计算/////////////////////
  // 售卖价格
  const sellUsdtAmount = formatEtherWithDecimals(agent.sellPriceUsdt);
  // 混合支付时 USDT 占基准价比例
  const mixedUsdtAmount =
    (BigInt(agent.sellPriceUsdt) * BigInt(10000 - agent.mixedFusdShareBp)) /
    BigInt(10000);
  // 混合支付时 FUSD 占基准价比例 u计价
  const mixedFusdUsdtValue =
    (BigInt(agent.sellPriceUsdt) * BigInt(agent.mixedFusdShareBp)) /
    BigInt(10000);
  // 混合支付时 FUSD 占基准价比例 fusd的量 这里加了0.01是为了避免浮点误差 导致用户划转少了 买不了
  const mixedFusdAmount =
    fusdtPrice && fusdtPrice > 0
      ? parseFloat(
          (
            +formatEtherWithDecimals(mixedFusdUsdtValue) / fusdtPrice +
            0.1
          ).toFixed(2)
        )
      : "--";
  // 本单混合 FUSD 应付总量（单价 mixedFusdAmount × 数量）；无有效单价时为 null
  const mixFusdLineTotal =
    typeof mixedFusdAmount === "number" ? mixedFusdAmount * quantity : null;
  // 明细区「待补/扣除总额」展示用字符串；无总量时显示 "--"
  const mixedFusdPendingDisplay =
    mixFusdLineTotal !== null ? mixFusdLineTotal.toFixed(2) : "--";
  // 从总量拆出的两笔需求（与 GetBalanceV2 的 gameFusd / waitClaim 对应）：约 1/3 与 2/3，各保留两位小数
  let mixedGameNeed: number | null = null;
  let mixedPendingNeed: number | null = null;
  if (mixFusdLineTotal !== null) {
    const third = mixFusdLineTotal / 3;
    mixedGameNeed = parseFloat(third.toFixed(2));
    mixedPendingNeed = parseFloat((mixFusdLineTotal - third).toFixed(2));
  }
  // 游戏余额是否覆盖 mixedGameNeed（1e-6 容差避免浮点误差）
  const gameBalanceSufficient =
    mixedGameNeed === null || userGameBalance >= mixedGameNeed - 1e-6;
  // 待提现是否覆盖 mixedPendingNeed
  const pendingBalanceSufficient =
    mixedPendingNeed === null ||
    userPendingWithdrawal >= mixedPendingNeed - 1e-6;
  // 非混合 FUSD 场景无 mixedGameNeed；混合时两路都够才算充足
  const fusdSufficient =
    mixedGameNeed === null ||
    (gameBalanceSufficient && pendingBalanceSufficient);
  // 混合支付且余额已拉取完毕、且 FUSD 不足时禁止下单
  const fusdMixBlocked =
    (paymentMode === "mixed" || paymentMode === "mixed_now") &&
    !balanceV2Loading &&
    mixedGameNeed !== null &&
    !fusdSufficient;
  /////////////////////// /////////////////////// ///////////////////////
  /////////////////////// 计算多个agent ///////////////////////
  // 总售卖价格
  const totalSellWei = BigInt(agent.sellPriceUsdt) * qtyBig;
  // 总基准价格
  const totalBaseWei = BigInt(agent.basePriceUsdt) * qtyBig;
  // 总混合支付时 USDT 价格
  const totalMixedUsdtWei = mixedUsdtAmount * qtyBig;
  // 总混合支付时 USDT 价格显示
  const displayUsdtPayAmount =
    paymentMode === "usdt"
      ? formatEtherWithDecimals(totalSellWei)
      : formatEtherWithDecimals(totalMixedUsdtWei);
  // 混合支付比例配置是否有效
  const mixedConfigInvalid =
    (paymentMode === "mixed" || paymentMode === "mixed_now") &&
    (!agent.supportsMixed ||
      agent.mixedFusdShareBp <= 0 ||
      agent.mixedFusdShareBp >= 10000);
  // 退出
  const resetAndClose = () => {
    setStep("form");
    setPendingKind("pay");
    setQuantity(1);
    setMixedKind("mixed");
    onClose();
  };
  // 购买
  const handleBuy = async () => {
    if (!address) {
      showError(t("homePage.buyAgentDialog.connectWalletFirst"));
      return;
    }
    if (mixedConfigInvalid) {
      showError(t("toast.fortAgent.purchase.result.mixedRatioConfigError"));
      return;
    }
    if (fusdMixBlocked) {
      showError(
        t("homePage.buyAgentDialog.fusdShortageTitle", {
          tokenName: getTokenName(CCC_FUSD_ADDRESS),
        })
      );
      return;
    }
    await switchChain(wagmiConfig, { chainId: bscChainId });
    setBuying(true);
    try {
      const res = await purchaseSignAsync({
        skuId: agent.skuId,
        paymentMode,
        quantity,
      });
      if (res.resultCode !== FortAgentPurchaseResultCode.Success) {
        return;
      }
      const requiredAmount = BigInt(res.amount);
      if (BigInt(usdtBalance ?? 0) < requiredAmount) {
        showError(t("homePage.buyAgentDialog.insufficientBalance"));
        return;
      }

      const currentAllowance = await readContract(wagmiConfig, {
        address: USDT_ADDRESS as `0x${string}`,
        abi: erc20Abi,
        functionName: "allowance",
        args: [address, FORTTRADE_CONTRACT_ADDRESS as `0x${string}`],
      });

      const needApprove = (currentAllowance as bigint) < requiredAmount;

      if (needApprove) {
        setStep("pending");
        setPendingKind("approve");
        const { request: approveRequest } = await simulateContract(
          wagmiConfig,
          {
            address: res.token as `0x${string}`,
            abi: erc20Abi,
            functionName: "approve",
            args: [FORTTRADE_CONTRACT_ADDRESS as `0x${string}`, requiredAmount],
          }
        );
        const approveHash = await writeContract.mutateAsync(approveRequest);
        await waitForTransactionReceipt(wagmiConfig, { hash: approveHash });
      }

      setStep("pending");
      setPendingKind("pay");
      const { request: purchaseRequest } = await simulateContract(wagmiConfig, {
        address: FORTTRADE_CONTRACT_ADDRESS as `0x${string}`,
        abi: FortTradeAbi,
        functionName: "purchaseBatch",
        args: [
          res.agentIds.map(id => BigInt(id)),
          res.token as `0x${string}`,
          requiredAmount,
          res.recipient as `0x${string}`,
          BigInt(res.deadline),
          BigInt(res.nonce),
          res.signature as `0x${string}`,
        ],
      });
      const purchaseHash = await writeContract.mutateAsync(purchaseRequest);
      await waitForTransactionReceipt(wagmiConfig, { hash: purchaseHash });

      queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
      setStep("success");
    } catch (err) {
      console.error("purchaseErr", err);
      setStep("form");
      const errorKey = parseChainError(err);
      showError(t(errorKey));
    } finally {
      setBuying(false);
    }
  };

  const handleSuccessViewAgents = () => {
    resetAndClose();
    setTimeout(() => {
      document
        .getElementById("my-agents-section")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  };

  const tierLabel = t(getAgentTierKey(agent.name));

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) resetAndClose();
      }}
    >
      <DialogContent
        className="max-w-sm p-0 overflow-hidden"
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          color: C.text,
        }}
      >
        {/* 顶栏：智能体图标、名称、档位 */}
        <div
          className="sticky top-0 z-10 px-4 pt-4 pb-2"
          style={{
            background: C.bg,
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <DialogHeader className="p-0">
            <DialogTitle className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden"
                style={{
                  background: `${tagColor}15`,
                  border: `1px solid ${tagColor}30`,
                }}
              >
                <img
                  src={AGENT_IMAGE_URL_ACTIVE}
                  alt=""
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <div className="text-sm font-bold" style={{ color: C.text }}>
                  {t("homePage.buyAgentDialog.title", { name: agent.name })}
                </div>
                <div
                  className="text-[10px] font-normal mt-0.5 px-1.5 py-0.5 rounded inline-block"
                  style={{ color: tagColor, background: `${tagColor}15` }}
                >
                  {tierLabel}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
        </div>

        <div
          className="px-4 pb-4 overflow-y-auto"
          style={{ maxHeight: "70vh" }}
        >
          <AnimatePresence mode="wait">
            {/* step=form：单价、数量、支付方式、订单摘要、FUSD 拆分、操作按钮 */}
            {step === "form" && (
              <motion.div
                key="form"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="space-y-2 pt-2"
              >
                {/* 单价卡片（含涨价时基础价与说明） */}
                <div
                  className="rounded-xl p-3"
                  style={{
                    background: `linear-gradient(135deg, ${C.card2}, rgba(17,22,40,0.8))`,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs" style={{ color: C.muted }}>
                      {t("homePage.buyAgentDialog.unitPrice")}
                    </span>
                    <span
                      className="mono-num font-bold text-base"
                      style={{ color: C.text }}
                    >
                      {formatUsdPrefix(
                        formatEtherWithDecimals(agent.sellPriceUsdt)
                      )}
                    </span>
                  </div>
                  {priceIncreased && (
                    <>
                      <div
                        className="h-px my-2"
                        style={{ background: C.border }}
                      />
                      <div className="flex justify-between items-center">
                        <span className="text-xs" style={{ color: C.muted }}>
                          {t("homePage.buyAgentDialog.basePrice")}
                        </span>
                        <span
                          className="mono-num text-sm"
                          style={{ color: C.muted }}
                        >
                          {formatUsdPrefix(
                            formatEtherWithDecimals(agent.basePriceUsdt)
                          )}
                        </span>
                      </div>
                      <div
                        className="text-[10px] px-2 py-1.5 rounded-lg mt-2"
                        style={{
                          background: "rgba(245,158,11,0.08)",
                          color: C.amber,
                          border: "1px solid rgba(245,158,11,0.12)",
                        }}
                      >
                        {t("homePage.agentDetailDialog.priceNote")}
                      </div>
                    </>
                  )}
                </div>

                {/* 数量步进器 */}
                <div
                  className="rounded-xl p-3"
                  style={{
                    background: `linear-gradient(135deg, ${C.card2}, rgba(17,22,40,0.8))`,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div className="flex justify-between items-center gap-2">
                    <div style={{ minWidth: 0 }}>
                      <span
                        className="text-xs font-semibold"
                        style={{ color: C.text }}
                      >
                        {t("homePage.buyAgentDialog.purchaseQuantity")}
                      </span>
                      <div
                        className="text-[10px] mt-0.5"
                        style={{ color: C.dim }}
                      >
                        {t("homePage.buyAgentDialog.remainingStock", {
                          left: agent.leftSupplyCount,
                          cap: agent.supplyCap,
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        disabled={!canDecQty}
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                        style={{
                          background: !canDecQty
                            ? "rgba(100,116,139,0.1)"
                            : "rgba(34,197,94,0.1)",
                          border: `1px solid ${!canDecQty ? "rgba(100,116,139,0.2)" : "rgba(34,197,94,0.3)"}`,
                          color: !canDecQty ? C.dim : C.primary,
                          cursor: !canDecQty ? "not-allowed" : "pointer",
                        }}
                      >
                        <Minus size={14} />
                      </button>
                      <span
                        className="mono-num font-bold text-lg w-8 text-center"
                        style={{ color: C.text }}
                      >
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setQuantity(q =>
                            maxQty >= 1 ? Math.min(maxQty, q + 1) : q
                          )
                        }
                        disabled={!canIncQty}
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition-all"
                        style={{
                          background: !canIncQty
                            ? "rgba(100,116,139,0.1)"
                            : "rgba(34,197,94,0.1)",
                          border: `1px solid ${!canIncQty ? "rgba(100,116,139,0.2)" : "rgba(34,197,94,0.3)"}`,
                          color: !canIncQty ? C.dim : C.primary,
                          cursor: !canIncQty ? "not-allowed" : "pointer",
                        }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  {quantity > 1 && (
                    <div
                      className="mt-2 pt-2"
                      style={{ borderTop: `1px solid ${C.border}` }}
                    >
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-[10px]" style={{ color: C.dim }}>
                          {formatUsdPrefix(
                            formatEtherWithDecimals(agent.sellPriceUsdt)
                          )}{" "}
                          × {quantity}
                        </span>
                        <span
                          className="mono-num font-bold text-sm shrink-0"
                          style={{ color: C.primary }}
                        >
                          {formatUsdPrefix(
                            formatEtherWithDecimals(totalSellWei)
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* 支付方式：新用户仅全额 USDT；老用户可选 mixed（延期 FUSD）/ mixed_now（即时扣 FUSD） */}
                <div className="space-y-1.5">
                  <div
                    className="text-xs font-semibold"
                    style={{ color: C.muted }}
                  >
                    {t("homePage.buyAgentDialog.paymentMethod")}
                  </div>
                  {paymentMode === "usdt" ? (
                    <div
                      className="w-full p-3 rounded-xl text-left relative overflow-hidden"
                      style={{
                        background: "rgba(34,197,94,0.08)",
                        border: `1.5px solid ${C.primary}`,
                        boxShadow: `0 0 12px rgba(34,197,94,0.08), inset 0 1px 0 rgba(34,197,94,0.1)`,
                      }}
                    >
                      <div
                        className="absolute left-0 top-0 bottom-0 w-[3px]"
                        style={{ background: C.primary }}
                      />
                      <div className="flex justify-between items-center pl-2">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded-full border-2 flex items-center justify-center"
                            style={{ borderColor: C.primary }}
                          >
                            <div
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ background: C.primary }}
                            />
                          </div>
                          <div>
                            <div
                              className="font-semibold text-xs"
                              style={{ color: C.text }}
                            >
                              {t("homePage.buyAgentDialog.fullUsdt", {
                                tokenNameUsdt: getTokenName(USDT_ADDRESS),
                              })}
                            </div>
                            {/* <div
                              className="text-[10px] mt-0.5"
                              style={{ color: C.dim }}
                            >
                              {t(
                                "homePage.buyAgentDialog.allUsersAvailable"
                              )}
                            </div> */}
                          </div>
                        </div>
                        <div
                          className="mono-num font-bold text-sm"
                          style={{ color: C.text }}
                        >
                          {formatUsdPrefix(sellUsdtAmount)}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // 老用户
                    <div className="space-y-1.5">
                      {/* 混合即时 */}
                      <button
                        type="button"
                        onClick={() => setMixedKind("mixed_now")}
                        className="w-full p-3 rounded-xl text-left transition-all relative overflow-hidden"
                        style={{
                          background:
                            mixedKind === "mixed_now"
                              ? "rgba(34,197,94,0.08)"
                              : "rgba(12,16,32,0.4)",
                          border:
                            mixedKind === "mixed_now"
                              ? `1.5px solid ${C.primary}`
                              : `1px solid ${C.border}`,
                          boxShadow:
                            mixedKind === "mixed_now"
                              ? `0 0 12px rgba(34,197,94,0.08), inset 0 1px 0 rgba(34,197,94,0.1)`
                              : "none",
                          opacity: mixedKind === "mixed_now" ? 1 : 0.65,
                        }}
                      >
                        {mixedKind === "mixed_now" && (
                          <div
                            className="absolute left-0 top-0 bottom-0 w-[3px]"
                            style={{ background: C.primary }}
                          />
                        )}
                        <div className="flex justify-between items-center gap-2 pl-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                              style={{
                                borderColor:
                                  mixedKind === "mixed_now" ? C.primary : C.dim,
                              }}
                            >
                              {mixedKind === "mixed_now" && (
                                <div
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ background: C.primary }}
                                />
                              )}
                            </div>
                            <div>
                              <div
                                className="font-semibold text-xs"
                                style={{
                                  color:
                                    mixedKind === "mixed_now"
                                      ? C.text
                                      : C.muted,
                                }}
                              >
                                {t("homePage.buyAgentDialog.mixedNowTitle", {
                                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                                  tokenNameUsdt: getTokenName(USDT_ADDRESS),
                                })}
                              </div>
                              {/* <div
                                className="text-[10px] mt-0.5"
                                style={{ color: C.dim }}
                              >
                                {t("homePage.buyAgentDialog.mixedNowDesc")}
                              </div> */}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div
                              className="mono-num font-bold text-xs"
                              style={{
                                color:
                                  mixedKind === "mixed_now" ? C.text : C.muted,
                              }}
                            >
                              {formatEtherWithDecimals(mixedUsdtAmount)} U
                            </div>
                            <div
                              className="mono-num text-[10px]"
                              // style={{ color: C.primary }}
                            >
                              {/* {t(
                                "homePage.buyAgentDialog.fusdImmediateShort",
                                { amount: mixedFusdAmount }
                              )} */}
                              {mixedFusdAmount} {getTokenName(CCC_FUSD_ADDRESS)}
                            </div>
                          </div>
                        </div>
                      </button>
                      {/* 暂时关闭混合预付 */}
                      {/* <button
                        type="button"
                        onClick={() => setMixedKind("mixed")}
                        className="w-full p-3 rounded-xl text-left transition-all relative overflow-hidden"
                        style={{
                          background:
                            mixedKind === "mixed"
                              ? "rgba(34,197,94,0.08)"
                              : "rgba(12,16,32,0.4)",
                          border:
                            mixedKind === "mixed"
                              ? `1.5px solid ${C.primary}`
                              : `1px solid ${C.border}`,
                          boxShadow:
                            mixedKind === "mixed"
                              ? `0 0 12px rgba(34,197,94,0.08), inset 0 1px 0 rgba(34,197,94,0.1)`
                              : "none",
                          opacity: mixedKind === "mixed" ? 1 : 0.65,
                        }}
                      >
                        {mixedKind === "mixed" && (
                          <div
                            className="absolute left-0 top-0 bottom-0 w-[3px]"
                            style={{ background: C.primary }}
                          />
                        )}
                        <div className="flex justify-between items-center gap-2 pl-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                              style={{
                                borderColor:
                                  mixedKind === "mixed" ? C.primary : C.dim,
                              }}
                            >
                              {mixedKind === "mixed" && (
                                <div
                                  className="w-2.5 h-2.5 rounded-full"
                                  style={{ background: C.primary }}
                                />
                              )}
                            </div>
                            <div>
                              <div
                                className="font-semibold text-xs"
                                style={{
                                  color:
                                    mixedKind === "mixed" ? C.text : C.muted,
                                }}
                              >
                                {t("homePage.buyAgentDialog.usdtPrepay", {
                                  tokenNameUsdt: getTokenName(USDT_ADDRESS),
                                })}
                              </div>
                              <div
                                className="text-[10px] mt-0.5"
                                style={{ color: C.dim }}
                              >
                                {t("homePage.buyAgentDialog.prepayDesc", {
                                  pct: (10000 - agent.mixedFusdShareBp) / 100,
                                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                                  tokenNameUsdt: getTokenName(USDT_ADDRESS),
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div
                              className="mono-num font-bold text-xs"
                              style={{
                                color: mixedKind === "mixed" ? C.text : C.muted,
                              }}
                            >
                              {formatEtherWithDecimals(mixedUsdtAmount)} U
                            </div>
                            <div
                              className="mono-num text-[10px]"
                              style={{ color: C.amber }}
                            >
                              {t("homePage.buyAgentDialog.fusdPending", {
                                amount: mixedFusdAmount,
                                tokenName: getTokenName(CCC_FUSD_ADDRESS),
                              })}
                            </div>
                          </div>
                        </div>
                      </button> */}
                    </div>
                  )}
                </div>

                {/* 订单确认：智能体、支付方式、支付金额、收益基准 */}
                <div
                  className="rounded-xl p-3 text-sm"
                  style={{
                    background: `linear-gradient(135deg, ${C.card2}, rgba(17,22,40,0.8))`,
                    border: `1px solid ${C.border}`,
                  }}
                >
                  {(
                    [
                      {
                        k: t("homePage.buyAgentDialog.agentLabel"),
                        v: (
                          <span>
                            {agent.name}
                            <span
                              className="ml-1 text-[10px] px-1 py-0.5 rounded"
                              style={{
                                color: tagColor,
                                background: `${tagColor}15`,
                              }}
                            >
                              {tierLabel}
                            </span>
                            {quantity > 1 && (
                              <span
                                className="ml-1 text-[10px]"
                                style={{ color: C.primary }}
                              >
                                ×{quantity}
                              </span>
                            )}
                          </span>
                        ),
                      },
                      {
                        k: t("homePage.buyAgentDialog.paymentMethod"),
                        v: (
                          <span>
                            {paymentMode === "usdt"
                              ? t("homePage.buyAgentDialog.fullUsdt", {
                                  tokenNameUsdt: getTokenName(USDT_ADDRESS),
                                })
                              : paymentMode === "mixed"
                                ? t("homePage.buyAgentDialog.prepayMethod", {
                                    pct: (10000 - agent.mixedFusdShareBp) / 100,
                                    tokenNameUsdt: getTokenName(USDT_ADDRESS),
                                  })
                                : t("homePage.buyAgentDialog.mixedNowMethod", {
                                    tokenName: getTokenName(CCC_FUSD_ADDRESS),
                                    tokenNameUsdt: getTokenName(USDT_ADDRESS),
                                  })}
                          </span>
                        ),
                      },
                      {
                        k: t("homePage.buyAgentDialog.paymentAmount"),
                        v:
                          paymentMode === "mixed" ? (
                            <span className="text-right inline-block">
                              <span className="block mono-num font-semibold">
                                {displayUsdtPayAmount}{" "}
                                {getTokenName(USDT_ADDRESS)}
                              </span>
                              <span
                                className="block mono-num text-[10px] mt-0.5"
                                style={{ color: C.amber }}
                              >
                                {t("homePage.buyAgentDialog.fusdPending", {
                                  amount: mixedFusdPendingDisplay,
                                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                                })}
                              </span>
                              <span
                                className="block text-[10px] mt-0.5"
                                style={{ color: C.dim }}
                              >
                                {t("homePage.buyAgentDialog.fusdPendingNote", {
                                  tokenName: getTokenName(CCC_FUSD_ADDRESS),
                                })}
                              </span>
                            </span>
                          ) : paymentMode === "mixed_now" ? (
                            <span className="text-right inline-block">
                              <span className="block mono-num font-semibold">
                                {displayUsdtPayAmount}{" "}
                                {getTokenName(USDT_ADDRESS)}
                              </span>
                              <span className="block mono-num text-[10px] mt-0.5">
                                {/* {t("homePage.buyAgentDialog.fusdImmediate", {
                                  amount: mixedFusdPendingDisplay,
                                })} */}
                                {mixedFusdPendingDisplay}{" "}
                                {getTokenName(CCC_FUSD_ADDRESS)}
                              </span>
                              {/* <span
                                className="block text-[10px] mt-0.5"
                                style={{ color: C.dim }}
                              >
                                {t(
                                  "homePage.buyAgentDialog.fusdImmediateNote"
                                )}
                              </span> */}
                            </span>
                          ) : (
                            <span className="mono-num font-semibold">
                              {formatUsdPrefix(displayUsdtPayAmount)}
                            </span>
                          ),
                      },
                      {
                        k: t("homePage.buyAgentDialog.earningsBase"),
                        v: (
                          <span
                            className="mono-num font-semibold"
                            style={{ color: C.green }}
                          >
                            {formatUsdPrefix(
                              formatEtherWithDecimals(totalBaseWei)
                            )}
                          </span>
                        ),
                      },
                    ] as const
                  ).map((item, i, arr) => (
                    <div key={item.k}>
                      <div className="flex justify-between items-start py-2">
                        <span
                          className="text-xs shrink-0"
                          style={{ color: C.muted }}
                        >
                          {item.k}
                        </span>
                        <span className="text-xs font-medium text-right ml-4">
                          {item.v}
                        </span>
                      </div>
                      {i < arr.length - 1 && (
                        <div
                          className="h-px"
                          style={{ background: C.border }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* 混合购 FUSD：待补(琥珀) / 即时扣除(绿)；下方为游戏余额与待提现拆分 */}
                {(paymentMode === "mixed" || paymentMode === "mixed_now") && (
                  <div
                    className="rounded-xl p-3 space-y-1.5 text-xs"
                    style={
                      paymentMode === "mixed"
                        ? {
                            background: "rgba(245,158,11,0.08)",
                            border: "1.5px solid rgba(245,158,11,0.3)",
                          }
                        : {
                            background: "rgba(34,197,94,0.08)",
                            border: "1.5px solid rgba(34,197,94,0.28)",
                          }
                    }
                  >
                    <div
                      className="flex items-center gap-2 font-bold mb-2"
                      style={{
                        color: paymentMode === "mixed" ? "#f59e0b" : C.primary,
                      }}
                    >
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{
                          background:
                            paymentMode === "mixed"
                              ? "rgba(245,158,11,0.15)"
                              : "rgba(34,197,94,0.15)",
                        }}
                      >
                        {paymentMode === "mixed" ? (
                          <AlertCircle size={13} />
                        ) : (
                          <Info size={13} />
                        )}
                      </div>
                      {paymentMode === "mixed"
                        ? t("homePage.buyAgentDialog.fusdDetailTitle", {
                            tokenName: getTokenName(CCC_FUSD_ADDRESS),
                          })
                        : t("homePage.buyAgentDialog.fusdDeductDetailTitle", {
                            tokenName: getTokenName(CCC_FUSD_ADDRESS),
                          })}
                    </div>
                    <div
                      className="space-y-2 text-[11px]"
                      style={{ color: C.text }}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">
                          {paymentMode === "mixed"
                            ? t("homePage.buyAgentDialog.fusdTotal")
                            : t("homePage.buyAgentDialog.fusdDeductTotal")}
                        </span>
                        <span
                          className="mono-num font-bold text-xs"
                          style={{
                            color:
                              paymentMode === "mixed" ? "#f59e0b" : C.primary,
                          }}
                        >
                          {mixedFusdPendingDisplay}{" "}
                          {getTokenName(CCC_FUSD_ADDRESS)}
                        </span>
                      </div>
                      <div
                        className="h-px"
                        style={{
                          background:
                            paymentMode === "mixed"
                              ? "rgba(245,158,11,0.12)"
                              : "rgba(34,197,94,0.15)",
                        }}
                      />
                      <div
                        className="flex justify-between items-center"
                        style={{ color: C.muted }}
                      >
                        <span>
                          {t("homePage.buyAgentDialog.fusdFromGame", {
                            tokenName: getTokenName(CCC_FUSD_ADDRESS),
                          })}
                        </span>
                        <span className="mono-num">
                          {mixedGameNeed ?? "—"}{" "}
                          {getTokenName(CCC_FUSD_ADDRESS)}
                        </span>
                      </div>
                      <div
                        className="flex justify-between items-center"
                        style={{ color: C.muted }}
                      >
                        <span>
                          {t("homePage.buyAgentDialog.fusdFromWithdraw", {
                            tokenName: getTokenName(CCC_FUSD_ADDRESS),
                          })}
                        </span>
                        <span className="mono-num">
                          {mixedPendingNeed ?? "—"}{" "}
                          {getTokenName(CCC_FUSD_ADDRESS)}
                        </span>
                      </div>
                    </div>

                    {!balanceV2Loading && mixedGameNeed !== null && (
                      <>
                        {!fusdSufficient && (
                          <div
                            className="mt-2 p-2.5 rounded-lg flex items-start gap-2"
                            style={{
                              background: "rgba(239,68,68,0.08)",
                              border: "1px solid rgba(239,68,68,0.25)",
                            }}
                          >
                            <AlertCircle
                              size={14}
                              className="shrink-0 mt-0.5"
                              style={{ color: C.red }}
                            />
                            <div
                              className="text-[11px] leading-relaxed"
                              style={{ color: C.red }}
                            >
                              <div className="font-bold mb-1">
                                {t(
                                  "homePage.buyAgentDialog.fusdShortageTitle",
                                  {
                                    tokenName: getTokenName(CCC_FUSD_ADDRESS),
                                  }
                                )}
                              </div>
                              {!gameBalanceSufficient &&
                                mixedGameNeed !== null && (
                                  <div>
                                    {t(
                                      "homePage.buyAgentDialog.fusdGameShortageLine",
                                      {
                                        tokenName:
                                          getTokenName(CCC_FUSD_ADDRESS),
                                        current: formatUSDT(userGameBalance),
                                        need: formatUSDT(mixedGameNeed),
                                        diff: formatUSDT(
                                          Math.max(
                                            0,
                                            mixedGameNeed - userGameBalance
                                          )
                                        ),
                                      }
                                    )}
                                  </div>
                                )}
                              {!pendingBalanceSufficient &&
                                mixedPendingNeed !== null && (
                                  <div>
                                    {t(
                                      "homePage.buyAgentDialog.fusdPendingShortageLine",
                                      {
                                        tokenName:
                                          getTokenName(CCC_FUSD_ADDRESS),
                                        current: formatUSDT(
                                          userPendingWithdrawal
                                        ),
                                        need: formatUSDT(mixedPendingNeed),
                                        diff: formatUSDT(
                                          Math.max(
                                            0,
                                            mixedPendingNeed -
                                              userPendingWithdrawal
                                          )
                                        ),
                                      }
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        )}
                        {fusdSufficient && (
                          <div
                            className="mt-2 p-2 rounded-lg flex items-center gap-2"
                            style={{
                              background: "rgba(34,197,94,0.06)",
                              border: "1px solid rgba(34,197,94,0.15)",
                            }}
                          >
                            <CheckCircle2
                              size={13}
                              style={{ color: C.green }}
                            />
                            <span
                              className="text-[11px]"
                              style={{ color: C.green }}
                            >
                              {t("homePage.buyAgentDialog.fusdSufficientHint", {
                                tokenName: getTokenName(CCC_FUSD_ADDRESS),
                              })}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* 链上提示 + 取消 / 确认支付 */}
                <div
                  className="text-[10px] text-center px-1"
                  style={{ color: C.dim }}
                >
                  {t("homePage.buyAgentDialog.confirmHint")}
                </div>

                <div className="flex gap-3 pt-1">
                  <Button
                    variant="outline"
                    className="flex-1 text-xs h-10"
                    onClick={resetAndClose}
                    style={{
                      borderColor: "rgba(100,140,200,0.15)",
                      color: C.muted,
                      background: "rgba(12,16,32,0.5)",
                    }}
                  >
                    {t("homePage.buyAgentDialog.cancel")}
                  </Button>
                  <button
                    type="button"
                    className="flex-1 h-10 rounded-lg font-bold text-sm btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleBuy}
                    disabled={
                      buying ||
                      mixedConfigInvalid ||
                      maxQty < 1 ||
                      fusdMixBlocked
                    }
                  >
                    {buying
                      ? t("homePage.buyAgentDialog.buying")
                      : fusdMixBlocked
                        ? t("homePage.buyAgentDialog.confirmPayFusdBlocked", {
                            tokenName: getTokenName(CCC_FUSD_ADDRESS),
                          })
                        : t("homePage.buyAgentDialog.confirmPay")}
                  </button>
                </div>
              </motion.div>
            )}

            {/* step=pending：USDT 授权或合约支付 */}
            {step === "pending" && (
              <motion.div
                key="pending"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-8 flex flex-col items-center gap-3"
              >
                <div
                  className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin"
                  style={{
                    borderColor: `${C.primary}33`,
                    borderTopColor: C.primary,
                  }}
                />
                <div className="text-center px-2">
                  <p className="font-bold">
                    {pendingKind === "approve"
                      ? t("homePage.buyAgentDialog.pendingApprove")
                      : t("homePage.buyAgentDialog.pendingPay")}
                  </p>
                  <p
                    className="text-xs mt-1 leading-relaxed"
                    style={{ color: C.dim }}
                  >
                    {pendingKind === "approve"
                      ? t("homePage.buyAgentDialog.pendingHintApprove")
                      : t("homePage.buyAgentDialog.pendingHintPay")}
                  </p>
                </div>
              </motion.div>
            )}

            {/* step=success：购买成功，跳转我的智能体 */}
            {step === "success" && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-6 flex flex-col items-center gap-3"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(34,197,94,0.1)" }}
                >
                  <CheckCircle2 size={32} style={{ color: C.green }} />
                </div>
                <div className="text-center px-2">
                  <h3 className="font-bold text-lg">
                    {t("homePage.buyAgentDialog.purchaseSuccess")}
                  </h3>
                  <p
                    className="text-xs mt-2 leading-relaxed"
                    style={{ color: C.muted }}
                  >
                    {t("homePage.buyAgentDialog.purchaseSuccessDesc", {
                      name: agent.name,
                      qtyPrefix:
                        quantity > 1
                          ? t(
                              "homePage.buyAgentDialog.purchaseSuccessQtyPrefix",
                              { count: quantity }
                            )
                          : "",
                    })}
                  </p>
                </div>
                <Button
                  className="w-full btn-primary"
                  onClick={handleSuccessViewAgents}
                >
                  {t("homePage.buyAgentDialog.viewMyAgents")}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
